import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { bonusTrackerAPI } from '../../services/api';
import { CustomerPayment, CustomerPaymentDetail, FUNDING_LABEL, FundingRecord, errText, fundingColor, money, todayYmd } from './bonusTypes';

interface Props {
  /** Accounting (or a super user) records customer payments; everyone else with access reads. */
  canRecord: boolean;
  onOpenBonus: (recordId: string) => void;
  onChanged?: () => void;
}

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

const FundingPanel: React.FC<Props> = ({ canRecord, onOpenBonus, onChanged }) => {
  const [records, setRecords] = useState<FundingRecord[]>([]);
  const [payments, setPayments] = useState<CustomerPayment[]>([]);
  const [show, setShow] = useState<'open' | 'all'>('open');
  const [payStatus, setPayStatus] = useState('ACTIVE');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [recording, setRecording] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [r, p] = await Promise.all([bonusTrackerAPI.fundingRecords(show === 'open'), bonusTrackerAPI.customerPayments(payStatus)]);
      setRecords(r);
      setPayments(p);
    } catch (e) { setMessage({ type: 'error', text: errText(e) }); }
  }, [show, payStatus]);
  useEffect(() => { void load(); }, [load]);
  const changed = (text?: string) => { if (text) setMessage({ type: 'success', text }); void load(); onChanged?.(); };

  return (
    <Box>
      {message && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Accounting records the payments a customer made to TEKS and matches them to the bonuses the customer pays for. This is separate from the approvals and from payroll:
          the employee is paid whether or not the customer has reimbursed TEKS yet. Payments and matches are never deleted; they are voided with a reason.
        </Typography>
        {canRecord && <Button variant="contained" sx={{ flexShrink: 0 }} onClick={() => setRecording(true)}>Record customer payment</Button>}
      </Stack>
      {!canRecord && <Alert severity="info" sx={{ mb: 2 }}>Only accounting (or a super user) records customer payments.</Alert>}

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Bonuses paid by the customer</Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Show</InputLabel>
          <Select label="Show" value={show} onChange={(e) => setShow(e.target.value as 'open' | 'all')}>
            <MenuItem value="open">Still to be paid by the customer</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Bonus</TableCell><TableCell>Period ends</TableCell><TableCell>Campaign</TableCell>
              <TableCell align="right">Due</TableCell><TableCell align="right">Received</TableCell><TableCell align="right">Still to receive</TableCell><TableCell>Customer</TableCell><TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((r) => (
              <TableRow key={r.recordId} hover>
                <TableCell>{r.programName}</TableCell>
                <TableCell>{r.periodEnd}</TableCell>
                <TableCell>{r.campaignName}</TableCell>
                <TableCell align="right">{money(r.due, r.currency)}</TableCell>
                <TableCell align="right">{money(r.funded, r.currency)}</TableCell>
                <TableCell align="right">{money(r.outstanding, r.currency)}</TableCell>
                <TableCell><Chip size="small" variant="outlined" color={fundingColor(r.state)} label={FUNDING_LABEL[r.state]} /></TableCell>
                <TableCell align="right"><Button size="small" onClick={() => onOpenBonus(r.recordId)}>Open bonus</Button></TableCell>
              </TableRow>
            ))}
            {!records.length && <TableRow><TableCell colSpan={8}><Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>{show === 'open' ? 'Nothing is waiting for the customer to pay.' : 'No customer-paid bonuses yet.'}</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Customer payments</Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={payStatus} onChange={(e) => setPayStatus(e.target.value)}>
            <MenuItem value="ACTIVE">Recorded</MenuItem>
            <MenuItem value="VOIDED">Voided</MenuItem>
            <MenuItem value="ALL">All</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell><TableCell>Customer</TableCell><TableCell>Reference</TableCell>
              <TableCell align="right">Amount</TableCell><TableCell align="right">Matched</TableCell><TableCell align="right">Not matched yet</TableCell><TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell>{p.paymentDate}</TableCell>
                <TableCell>{p.customerName}{p.status === 'VOIDED' && <Chip size="small" color="error" label="Voided" sx={{ ml: 1 }} />}</TableCell>
                <TableCell>{p.reference}</TableCell>
                <TableCell align="right">{money(p.amount, p.currency)}</TableCell>
                <TableCell align="right">{money(p.allocated, p.currency)}</TableCell>
                <TableCell align="right">{p.status === 'VOIDED' ? '-' : money(p.unallocated, p.currency)}</TableCell>
                <TableCell align="right"><Button size="small" onClick={() => setOpenId(p.id)}>Open</Button></TableCell>
              </TableRow>
            ))}
            {!payments.length && <TableRow><TableCell colSpan={7}><Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No customer payments recorded.</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>

      {recording && <RecordPayment onClose={() => setRecording(false)} onSaved={() => { setRecording(false); changed('Customer payment recorded.'); }} />}
      {openId && <PaymentDetail id={openId} canRecord={canRecord} onClose={() => setOpenId(null)} onChanged={changed} />}
    </Box>
  );
};

/* ------------------------------ record a payment ------------------------------ */

const RecordPayment: React.FC<{ onClose: () => void; onSaved: () => void }> = ({ onClose, onSaved }) => {
  const [f, setF] = useState({ customerName: '', paymentDate: todayYmd(), amount: '', currency: 'USD', reference: '', note: '' });
  const [open, setOpen] = useState<FundingRecord[]>([]);
  const [match, setMatch] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { void (async () => { try { setOpen(await bonusTrackerAPI.fundingRecords(true)); } catch (e) { setError(errText(e)); } })(); }, []);

  const amount = Number(f.amount) || 0;
  const matched = round2(Object.values(match).reduce((s, v) => s + (Number(v) || 0), 0));
  const candidates = open.filter((r) => r.currency.toUpperCase() === f.currency.toUpperCase());

  const autoMatch = () => {
    let left = amount;
    const next: Record<string, string> = {};
    [...candidates].sort((a, b) => a.periodEnd.localeCompare(b.periodEnd)).forEach((r) => {
      if (left <= 0) return;
      const part = round2(Math.min(left, r.outstanding));
      if (part > 0) { next[r.recordId] = String(part); left = round2(left - part); }
    });
    setMatch(next);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const allocations = Object.entries(match).filter(([, v]) => Number(v) > 0).map(([recordId, v]) => ({ recordId, amount: Number(v) }));
      await bonusTrackerAPI.createCustomerPayment({ ...f, amount: Number(f.amount), allocations });
      onSaved();
    } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Record customer payment</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '16px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField size="small" required fullWidth label="Customer that paid" value={f.customerName} onChange={(e) => setF({ ...f, customerName: e.target.value })} />
          <TextField size="small" required fullWidth label="Invoice or payment reference" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField size="small" required type="date" fullWidth label="Date paid" InputLabelProps={{ shrink: true }} value={f.paymentDate} onChange={(e) => setF({ ...f, paymentDate: e.target.value })} inputProps={{ max: todayYmd() }} />
          <TextField size="small" required type="number" fullWidth label="Amount received" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />
          <TextField size="small" label="Currency" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value.toUpperCase().slice(0, 3) })} sx={{ minWidth: 100 }} />
        </Stack>
        <TextField size="small" label="Note (optional)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2">Match to bonuses (optional, can be done later)</Typography>
            <Button size="small" disabled={!(amount > 0)} onClick={autoMatch}>Match oldest first</Button>
          </Stack>
          <Table size="small">
            <TableHead><TableRow><TableCell>Bonus</TableCell><TableCell>Period ends</TableCell><TableCell align="right">Still to receive</TableCell><TableCell align="right">Match</TableCell></TableRow></TableHead>
            <TableBody>
              {candidates.map((r) => (
                <TableRow key={r.recordId}>
                  <TableCell>{r.programName}</TableCell>
                  <TableCell>{r.periodEnd}</TableCell>
                  <TableCell align="right">{money(r.outstanding, r.currency)}</TableCell>
                  <TableCell align="right"><TextField size="small" type="number" sx={{ width: 120 }} value={match[r.recordId] ?? ''} onChange={(e) => setMatch({ ...match, [r.recordId]: e.target.value })} inputProps={{ min: 0, step: '0.01' }} /></TableCell>
                </TableRow>
              ))}
              {!candidates.length && <TableRow><TableCell colSpan={4}><Typography color="text.secondary" variant="body2">No bonus waits for this customer in {f.currency}.</Typography></TableCell></TableRow>}
            </TableBody>
          </Table>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 700 }} color={matched > amount ? 'error' : 'text.primary'}>Matched {money(matched, f.currency)} of {money(amount, f.currency)}</Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || !f.customerName.trim() || !f.reference.trim() || !(amount > 0) || matched > amount}>Record payment</Button>
      </DialogActions>
    </Dialog>
  );
};

/* ------------------------------ payment detail ------------------------------ */

const PaymentDetail: React.FC<{ id: string; canRecord: boolean; onClose: () => void; onChanged: (text?: string) => void }> = ({ id, canRecord, onClose, onChanged }) => {
  const [p, setP] = useState<CustomerPaymentDetail | null>(null);
  const [open, setOpen] = useState<FundingRecord[]>([]);
  const [recordId, setRecordId] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setP(await bonusTrackerAPI.customerPayment(id));
      setOpen(await bonusTrackerAPI.fundingRecords(true));
    } catch (e) { setError(errText(e)); }
  }, [id]);
  useEffect(() => { void load(); }, [load]);

  const act = async (fn: () => Promise<CustomerPaymentDetail>, text: string) => {
    setBusy(true);
    setError(null);
    try { setP(await fn()); setReason(''); setAmount(''); setRecordId(''); setOpen(await bonusTrackerAPI.fundingRecords(true)); onChanged(text); } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };

  const candidates = p ? open.filter((r) => r.currency.toUpperCase() === p.currency.toUpperCase()) : [];
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{p ? `${p.customerName} · ${p.reference}` : 'Customer payment'}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '16px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        {p && (
          <>
            <Typography variant="body2">
              {money(p.amount, p.currency)} received on {p.paymentDate} · recorded by {p.recordedByName || 'unknown'} · matched {money(p.allocated, p.currency)}
              {p.status === 'ACTIVE' ? ` · not matched yet ${money(p.unallocated, p.currency)}` : ''}
            </Typography>
            {p.note && <Typography variant="body2" color="text.secondary">{p.note}</Typography>}
            {p.status === 'VOIDED' && <Alert severity="error">Voided by {p.voidedByName}: {p.voidReason}</Alert>}
            <Table size="small">
              <TableHead><TableRow><TableCell>Matched to</TableCell><TableCell>Period ends</TableCell><TableCell align="right">Amount</TableCell><TableCell>State</TableCell><TableCell /></TableRow></TableHead>
              <TableBody>
                {p.allocations.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>{a.programName}</TableCell>
                    <TableCell>{a.periodEnd}</TableCell>
                    <TableCell align="right">{money(a.amount, p.currency)}</TableCell>
                    <TableCell>{a.status === 'ACTIVE' ? 'Matched' : <Typography variant="caption" color="error">Voided · {a.voidReason}</Typography>}</TableCell>
                    <TableCell align="right">
                      {a.status === 'ACTIVE' && canRecord && <Button size="small" color="warning" disabled={busy || !reason.trim()} onClick={() => void act(() => bonusTrackerAPI.voidCustomerAllocation(a.id, reason), 'Match voided.')}>Void match</Button>}
                    </TableCell>
                  </TableRow>
                ))}
                {!p.allocations.length && <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">Not matched to any bonus yet.</Typography></TableCell></TableRow>}
              </TableBody>
            </Table>
            {p.status === 'ACTIVE' && canRecord && (
              <>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Match to a bonus</Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Bonus</InputLabel>
                      <Select label="Bonus" value={recordId} onChange={(e) => {
                        setRecordId(e.target.value);
                        const r = candidates.find((x) => x.recordId === e.target.value);
                        if (r) setAmount(String(round2(Math.min(r.outstanding, p.unallocated))));
                      }}>
                        {candidates.map((r) => <MenuItem key={r.recordId} value={r.recordId}>{r.programName} · ends {r.periodEnd} · {money(r.outstanding, r.currency)} left</MenuItem>)}
                      </Select>
                    </FormControl>
                    <TextField size="small" type="number" label="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} sx={{ minWidth: 140 }} />
                    <Button variant="outlined" disabled={busy || !recordId || !(Number(amount) > 0)} onClick={() => void act(() => bonusTrackerAPI.allocateCustomerPayment(id, recordId, Number(amount)), 'Payment matched to the bonus.')}>Match</Button>
                  </Stack>
                </Paper>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField size="small" fullWidth label="Reason (needed to void a match or the payment)" value={reason} onChange={(e) => setReason(e.target.value)} />
                  <Button color="error" variant="outlined" sx={{ whiteSpace: 'nowrap' }} disabled={busy || !reason.trim()} onClick={() => void act(() => bonusTrackerAPI.voidCustomerPayment(id, reason), 'Payment voided.')}>Void payment</Button>
                </Stack>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
};

export default FundingPanel;
